import type { AgencyId, UserId } from "@inrev/common";
import {
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import { AgentSelectorInput } from "../inputs/AgentSelectorInput";

export type FormAgentSelectorProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	agencyId: AgencyId | "" | null | undefined;
	includeInactive?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	compact?: boolean;
	optionsClassName?: string;
	placeholder?: string;
	errorMessage?: string;
	testId?: string;
};

export const FormAgentSelector = <
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
>({
	name,
	control,
	defaultValue,
	agencyId,
	includeInactive,
	disabled,
	readonly,
	compact,
	optionsClassName,
	placeholder,
	errorMessage,
	testId,
}: FormAgentSelectorProps<TFieldValues, TTransformedValues>) => {
	const {
		field: { ref: _ref, onBlur, onChange, value },
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
	});

	const hasError = !!error;

	const handleChange = (next: UserId | null) => {
		onChange(next);
		onBlur();
	};

	return (
		<div className="relative">
			<AgentSelectorInput
				agencyId={agencyId}
				value={value ?? null}
				onChange={handleChange}
				includeInactive={includeInactive}
				disabled={disabled}
				readonly={readonly}
				compact={compact}
				error={hasError}
				placeholder={placeholder}
				optionsClassName={optionsClassName}
				testId={testId}
			/>
			{hasError && <FormFieldError error={error} errorMessage={errorMessage} />}
		</div>
	);
};
